// JournalList.js
import React, { useState, useEffect, useRef } from "react";
import "../styles/JournalList.css";
import { FaSearch } from "react-icons/fa";
import { FcEmptyFilter, FcFilledFilter, FcClearFilters } from "react-icons/fc";
import {} from "react-icons/fa";

import "react-datepicker/dist/react-datepicker.css";

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = date.toLocaleString("default", { weekday: "short" });
  const dayOfMonth = date.getDate();
  return (
    <div className="date-square">
      <div className="day">{day}</div>
      <div className="date">{dayOfMonth}</div>
    </div>
  );
}

function groupEntriesByMonth(entries) {
	const grouped = {};
  
	// Group entries by month and year
	entries.forEach((entry) => {
	  const date = new Date(entry.date);
	  const monthYear = date.toLocaleString("default", {
		month: "long",
		year: "numeric",
	  });
	  if (!grouped[monthYear]) {
		grouped[monthYear] = [];
	  }
	  grouped[monthYear].push(entry);
	});
  
	// Convert grouped object to an array of { monthYear, entries }
	const groupedArray = Object.keys(grouped).map((monthYear) => ({
	  monthYear,
	  entries: grouped[monthYear],
	}));
  
	// Sort the grouped array in descending order
	groupedArray.sort((a, b) => {
	  const dateA = new Date(a.monthYear);
	  const dateB = new Date(b.monthYear);
	  return dateB - dateA; // Descending order (latest first)
	});
  
	return groupedArray;
  }
  
function JournalList({ entries, handleSelectEntry }) {
  const [currentHeader, setCurrentHeader] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [searchedEntries, setSearchedEntries] = useState(entries);
  const listRef = useRef(null);
  const filterRef = useRef(null);

  const handleFilterClick = () => {
    setIsFilterOpen(!isFilterOpen);
    if (!isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  };

  const handleClearFilters = () => {
    setSelectedYear("");
    setSelectedMonth("");
    handleFilterClick();
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const getThumbnail = (entry) => {
    return entry.image.replace("{width}", 150).replace("{height}", 150);
  };

  const handleClickOutside = (event) => {
    if (filterRef.current && !filterRef.current.contains(event.target)) {
      setIsFilterOpen(false);
      document.removeEventListener("mousedown", handleClickOutside);
    }
  };

  // Filter entries based on search query and selected date
  useEffect(() => {
    let filtered = entries;

    if (searchQuery) {
      filtered = filtered.filter(
        (entry) =>
          entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entry.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entry.country.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

	setSearchedEntries(filtered);
  }, [searchQuery, entries]);

  // Handle scroll to update header
  useEffect(() => {
    const handleScroll = () => {
      const listElement = listRef.current;
      if (!listElement) return;

      const entries = listElement.querySelectorAll(".journal-entry");
      let newHeader = "";

      entries.forEach((entry) => {
        const rect = entry.getBoundingClientRect();
        if (rect.top <= 400 && rect.bottom >= 0) {
          const monthYear = entry.getAttribute("data-month-year");
          newHeader = monthYear;
        }
      });

      if (newHeader && newHeader !== currentHeader) {
        setCurrentHeader(newHeader);
      }
    };

    const listElement = listRef.current;
    listElement.addEventListener("scroll", handleScroll);
    return () => listElement.removeEventListener("scroll", handleScroll);
  }, [currentHeader]);

  const filteredEntries = searchedEntries.filter((entry) => {
    const date = new Date(entry.date);
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString(); // Months are 0-indexed
    return (
      (!selectedYear || year === selectedYear) &&
      (!selectedMonth || month === selectedMonth)
    );
  });

  const groupedEntries = groupEntriesByMonth(filteredEntries);

  return (
    <div className="journal-list-container">
      {/* Filter and Search Bar */}
      <div className="filter-bar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {/* Filter Button and Dropdown */}
        <div className="filter-container" ref={filterRef}>
          <button className="filter-button" onClick={handleFilterClick}>
            {!selectedMonth && !selectedYear ? (
              <FcEmptyFilter title="Select filter to narrow down your search" />
            ) : (
              <FcFilledFilter />
            )}
          </button>
          {isFilterOpen && (
            <div className="filter-dropdown">
              <select value={selectedYear} onChange={handleYearChange}>
                <option value="">Select Year</option>
                {Array.from(
                  new Set(
                    entries.map((entry) => new Date(entry.date).getFullYear())
                  )
                )
                  .sort((a, b) => b - a)
                  .map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
              </select>
              <select value={selectedMonth} onChange={handleMonthChange}>
                <option value="">Select Month</option>
                {Array.from(
                  new Set(
                    entries.map((entry) => new Date(entry.date).getMonth() + 1)
                  )
                )
                  .sort((a, b) => a - b)
                  .map((month) => (
                    <option key={month} value={month}>
                      {new Date(0, month - 1).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
              </select>
              <div
                title="Clear Filters"
                onClick={() => handleClearFilters()}
                style={{ cursor: "pointer" }}
              >
                <FcClearFilters />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Header with Month and Year */}
      <div className="header">
        <div className="header-content">
          {currentHeader || groupedEntries[0].monthYear}
        </div>
        <hr />
      </div>

      {/* Scrollable List */}
      <div className="journal-list" ref={listRef}>
        {groupedEntries.map((journal) => (
          <div key={journal.monthYear} className="entry-group">
            {journal.entries.map((entry, index) => (
              <div
                onClick={() => handleSelectEntry(entry.id)}
                key={index}
                className="journal-entry"
                data-month-year={journal.monthYear}
              >
                {/* Left Section: Date Square */}
                {formatDate(entry.date)}

                {/* Middle Section: Title and Location */}
                <div className="entry-details">
                  <h3 className="entry-title">{entry.title}</h3>
                  <p className="entry-location">
                    {entry.location}, {entry.country}
                  </p>
                </div>

                {/* Right Section: Image */}
                <div className="entry-image">
                  <img src={getThumbnail(entry)} alt={entry.title} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default JournalList;
